import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card, Spinner, Empty, Badge } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Search, Filter } from "lucide-react";

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({ user_email: "", method: "", path_contains: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.method) params.method = filters.method;
      if (filters.path_contains) params.path_contains = filters.path_contains;
      const { data } = await api.get("/audit-logs", { params });
      setItems(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [filters]);

  const tone = (m) => m === "DELETE" ? "danger" : m === "POST" ? "success" : m === "PUT" ? "warn" : "info";
  const statusTone = (s) => s >= 500 ? "danger" : s >= 400 ? "warn" : "success";

  return (
    <Layout title="Auditoria" subtitle="Log automático de ações sensíveis (POST/PUT/DELETE)">
      <Card className="p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
            <input value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
              placeholder="Filtrar por usuário..." data-testid="audit-filter-user"
              className="w-full rounded-md bg-white border border-[#D6D2CC] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
          <select value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })}
            data-testid="audit-filter-method"
            className="rounded-md bg-white border border-[#D6D2CC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20">
            <option value="">Todos os métodos</option>
            <option value="POST">POST (criação)</option>
            <option value="PUT">PUT (edição)</option>
            <option value="DELETE">DELETE (exclusão)</option>
            <option value="PATCH">PATCH</option>
          </select>
          <div className="relative">
            <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
            <input value={filters.path_contains} onChange={(e) => setFilters({ ...filters, path_contains: e.target.value })}
              placeholder="Filtrar por endpoint..." data-testid="audit-filter-path"
              className="w-full rounded-md bg-white border border-[#D6D2CC] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
        </div>
      </Card>

      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}
      {loading ? <div className="flex justify-center py-20"><Spinner size={28} /></div> : items.length === 0 ? (
        <Card><Empty title="Nenhum log encontrado" hint="Realize ações no sistema para gerar registros de auditoria." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-tertiary">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">Método</th>
                <th className="px-4 py-3">Endpoint</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-tertiary last:border-0 hover:bg-stone-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-ink-700">{(l.ts || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="px-4 py-2.5 text-ink-900">{l.user_email}</td>
                  <td className="px-4 py-2.5"><Badge tone={tone(l.method)}>{l.method}</Badge></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-ink-700">{l.path}</td>
                  <td className="px-4 py-2.5 text-center"><Badge tone={statusTone(l.status_code)}>{l.status_code}</Badge></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-ink-500">{l.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </Layout>
  );
}
